import http from "@/Service/http-common";
import { CORRELATION_HEADER, deriveCorrelationId } from "./correlationHeaders";
import { uploadDiagnosticsRegistry } from "../diagnostics/UploadDiagnosticsRegistry";
import { UPLOAD_EVENT } from "../diagnostics/UploadEvents";
import { getAnalytics } from "../analytics";

/**
 * HttpBridge attaches axios interceptors to the shared http-common instance.
 * Responsibilities:
 *   - Stamp X-Correlation-Id on every request (mirrors X-Upload-Id when set)
 *   - Emit an api_request / api_response / api_error event into the matching
 *     UploadDiagnostics, if the request carries X-Upload-Id
 *   - Fire an analytics event (api_error) for any non-2xx on an upload call
 *
 * The bridge is designed to be a no-op for non-upload requests: they get the
 * correlation header but no per-request diagnostics recording, so we don't
 * flood the timeline with unrelated app API calls.
 *
 * install() is idempotent — subsequent calls skip re-attaching.
 */
let installed = false;

export const installHttpBridge = () => {
  if (installed) return;
  installed = true;

  http.interceptors.request.use((config) => {
    // 1. Correlation header (safe to call for every request).
    try {
      const correlationId = deriveCorrelationId(config);
      writeHeader(config, CORRELATION_HEADER, correlationId);
      // Attach start time so the response interceptor can compute duration.
      config.metadata = { ...(config.metadata || {}), startedAt: Date.now() };
    } catch (_error) {
      /* header writes must never break the request */
    }
    return config;
  });

  http.interceptors.response.use(
    (response) => {
      handleApiOutcome({ response, isError: false });
      return response;
    },
    (error) => {
      handleApiOutcome({ response: error?.response, error, isError: true });
      throw error;
    }
  );
};

const handleApiOutcome = ({ response, error, isError }) => {
  try {
    const config = response?.config || error?.config;
    if (!config) return;
    const uploadId = readHeader(config, "X-Upload-Id");
    const url = safeUrl(config);
    const method = (config.method || "get").toUpperCase();
    const status = response?.status ?? error?.response?.status ?? null;
    const startedAt = config.metadata?.startedAt || null;
    const durationMs = startedAt ? Date.now() - startedAt : null;

    const payload = {
      method,
      url,
      status,
      durationMs,
      correlationId: readHeader(config, CORRELATION_HEADER),
      requestId: readHeader(config, "x-request-id"),
    };

    // Analytics: record failures for every request (upload or not). Success is
    // too noisy — only recorded on upload calls via the diagnostics timeline.
    if (isError) {
      const analytics = getAnalytics();
      analytics.track(UPLOAD_EVENT.API_ERROR, {
        ...payload,
        message: error?.message,
        code: error?.code,
        uploadId: uploadId || undefined,
      });
    }

    if (uploadId) {
      const diag = uploadDiagnosticsRegistry.get(uploadId);
      if (diag) {
        diag.record(
          isError ? UPLOAD_EVENT.API_ERROR : UPLOAD_EVENT.API_RESPONSE,
          payload,
          { skipAnalytics: !isError }
        );
      }
    }
  } catch (_swallowed) {
    /* observers must never break the response pipeline */
  }
};

const writeHeader = (config, name, value) => {
  if (!config?.headers) config.headers = {};
  const headers = config.headers;
  if (typeof headers.set === "function") {
    headers.set(name, value);
    return;
  }
  headers[name] = value;
};

const readHeader = (config, name) => {
  if (!config?.headers) return null;
  const headers = config.headers;
  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase()) || null;
  }
  return headers[name] || headers[name.toLowerCase()] || null;
};

const safeUrl = (config) => {
  if (!config) return null;
  const url = config.url || "";
  const base = config.baseURL || "";
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${base}${url}`;
};

// Test-only reset.
export const __resetHttpBridgeForTests = () => {
  installed = false;
};
